import { makeStyles } from '@mui/styles';
import { Clear, ExpandMore, FilterList, FilterListOff, Layers, LayersClear, Refresh, Save, SwapVert } from '@mui/icons-material';
import { Badge, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
    GridFilterModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarExport,
    useGridApiContext
} from '@mui/x-data-grid-pro';
import { isSelectedDeletedRecords } from 'ui-component/records/utils/deleteHelpers';
import { GridToolbarEdit } from './GridToolbarEdit';
import { GridToolbarActions } from './GridToolbarActions';
import { GridToolbarPrint } from './GridToolbarPrint';
import { checkIsEditing } from 'ui-component/records/utils/editHelpers';
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import { GetZipFileSignedURL } from 'ui-component/records/types';
import useIsAllowed from 'hooks/useIsAllowed';
import { FIELDS_FORCE_TO_EXPORT } from 'ui-component/records/utils';
import { useRef, useState } from 'react';
import { ImportRecord as ImportRecordIcon } from 'ui-component/icons';
import { BasicMenuList } from 'ui-component/menus';
import { ImportRecord } from 'ui-component/records/ImportDialogs';

const useStyles = makeStyles(({ palette }) => ({
    gridOptionsButton: {
        padding: '8px 16px',
        color: palette.primary[400],
        backgroundColor: palette.primary[300],
        borderRadius: '30px'
    },
    gridToolbarCustomFont: { fontSize: '0.8125rem', fontWeight: '500', color: palette.primary[400], marginLeft: '4px' },
    gridToolbarCustomFontBlack: { fontSize: '0.8125rem', fontWeight: '500', color: palette.primary[400], marginLeft: '4px' }
}));

export interface ICustomToolbar {
    handleGridRefresh: () => Promise<void>;
    handleResetRowFilters: () => void;
    showResetRowOrder: boolean;
    handleResetRowOrder: () => void;
    onClickEdit: () => void;
    rowsEditModel: Record<string, any>;
    onSave: () => void;
    onDelete: () => Promise<void>;
    showOnlyEnabledRecords: boolean;
    onToggleShowRecords: () => void;
    recordType: string;
    onClickBulkEdit: () => void;
    updateLoading: boolean;
    rowsSelectedAmount: number;
    getZipFileUrl: LazyQueryExecFunction<GetZipFileSignedURL, OperationVariables>;
    isSuperRecordType?: boolean;
    appliedFilterModel?: GridFilterModel;
    recordTypeId: number;
    showObjectPanel: boolean;
    ObjectPanelContainer: () => JSX.Element;
    isOpenImportDialog: boolean;
    setIsOpenImportDialog: React.Dispatch<React.SetStateAction<boolean>>;
    objectsToCreate: Record<string, any[]>;
}

const MUI_FIELDS_TO_NOT_EXPORT = ['__check__', 'actions', '__detail_panel_toggle__'] as const;

export const CustomToolbar = ({
    handleGridRefresh,
    handleResetRowFilters,
    showResetRowOrder,
    handleResetRowOrder,
    onClickEdit,
    rowsEditModel,
    onSave,
    onDelete,
    showOnlyEnabledRecords,
    onToggleShowRecords,
    recordType,
    onClickBulkEdit,
    updateLoading,
    rowsSelectedAmount,
    getZipFileUrl,
    isSuperRecordType = false,
    appliedFilterModel,
    recordTypeId,
    showObjectPanel,
    ObjectPanelContainer,
    isOpenImportDialog,
    setIsOpenImportDialog,
    objectsToCreate
}: ICustomToolbar) => {
    const apiRef = useGridApiContext();
    const classes = useStyles();

    const [importAnchorRef, setImportAnchorRef] = useState<HTMLElement | null>(null);
    const [downloadTemplateAnchor, setDownloadTemplateAnchor] = useState<HTMLElement | null>(null);
    const [selectedSifFile, setSelectedSifFile] = useState<File | null>(null);
    const inpuSifRef = useRef<HTMLInputElement | null>(null);

    const { isAllowed: isAllowedDelete } = useIsAllowed('RECORD_SOFT_DELETE');
    const { isAllowed: isAllowedDownload } = useIsAllowed('BULK_DOWNLOAD');
    const { isAllowed: isAllowedUpdate } = useIsAllowed('BULK_UPDATE');
    const { isAllowed: isAllowedImport } = useIsAllowed('BULK_IMPORT_RECORDS');
    const { isAllowed: isAllowedEdit } = useIsAllowed('ENRICH_RECORD_DATA');

    const rowsSelected = apiRef.current.getSelectedRows();
    const isEditing = checkIsEditing(rowsEditModel);
    const isAlreadyDeleted = isSelectedDeletedRecords(rowsSelected);

    const visibleColumns = apiRef.current
        .getVisibleColumns()
        .filter((el) => !MUI_FIELDS_TO_NOT_EXPORT.includes(el.field as (typeof MUI_FIELDS_TO_NOT_EXPORT)[number]))
        .map((el) => el.field);

    const csvOptions = {
        fields: [...visibleColumns, ...FIELDS_FORCE_TO_EXPORT]
    };

    return (
        <GridToolbarContainer sx={{ gap: 0.5 }}>
            <GridToolbarColumnsButton
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '8px 16px'
                })}
                endIcon={<ExpandMore />}
            />

            <Button
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '8px 16px'
                })}
                onClick={(e) => {
                    e.preventDefault();
                    apiRef.current.showFilterPanel();
                }}
                startIcon={
                    <Badge badgeContent={appliedFilterModel?.items?.length ?? 0} color="primary">
                        <FilterList />
                    </Badge>
                }
                endIcon={<ExpandMore />}
            >
                Filters
            </Button>

            {appliedFilterModel && appliedFilterModel?.items?.length > 0 && (
                <Tooltip title="Reset Filters">
                    <IconButton
                        color="primary"
                        onClick={(e) => {
                            e.preventDefault();
                            handleResetRowFilters();
                        }}
                        className={classes.gridOptionsButton}
                    >
                        <FilterListOff />
                        <Typography variant="body1" className={classes.gridToolbarCustomFont} component="div">
                            Reset Filters
                        </Typography>
                        <Clear fontSize="small" className={classes.gridToolbarCustomFontBlack} />
                    </IconButton>
                </Tooltip>
            )}
            <GridToolbarDensitySelector
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '8px 16px'
                })}
                endIcon={<ExpandMore />}
            />
            <GridToolbarPrint
                sx={({ palette }: { palette: any }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '8px 16px'
                })}
            />
            <GridToolbarExport
                sx={(theme: { palette: { primary: any[] } }) => ({
                    color: theme.palette.primary[400],
                    backgroundColor: theme.palette.primary[300],
                    borderRadius: '30px',
                    padding: '8px 16px'
                })}
                endIcon={<ExpandMore />}
                csvOptions={csvOptions}
                printOptions={{
                    disableToolbarButton: true
                }}
            />
            {isAllowedImport && (
                <Tooltip title="Import">
                    <IconButton
                        color="primary"
                        onClick={(e) => {
                            e.preventDefault();
                            setImportAnchorRef(e.currentTarget);
                        }}
                        className={classes.gridOptionsButton}
                    >
                        <ImportRecordIcon sx={{ fontSize: '18px' }} />
                        <Typography variant="body1" className={classes.gridToolbarCustomFont} component="div">
                            Import
                        </Typography>
                        <ExpandMore fontSize="small" sx={{ ml: '6px' }} />
                    </IconButton>
                </Tooltip>
            )}
            <>
                <BasicMenuList
                    open={!!importAnchorRef}
                    anchorEl={importAnchorRef}
                    onClose={() => setImportAnchorRef(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    items={[
                        { text: 'Import', onClick: () => document.getElementById('sifFileInput')?.click() }
                        // {
                        //     text: 'Download template',
                        //     noAutoClose: true,
                        //     showChevron: true,
                        //     onClick: (e) => setDownloadTemplateAnchor(e?.currentTarget || null)
                        // }
                    ]}
                />
                <BasicMenuList
                    open={!!downloadTemplateAnchor}
                    anchorEl={downloadTemplateAnchor}
                    onClose={() => setDownloadTemplateAnchor(null)}
                    anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                    items={[{ text: 'SIF' }]}
                />
                <input
                    type="file"
                    name="sifFile"
                    id="sifFileInput"
                    accept=".sif"
                    ref={inpuSifRef}
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                            setSelectedSifFile(event.target.files?.[0] || null);
                            setIsOpenImportDialog(true);
                        }
                    }}
                    style={{ display: 'none' }}
                />
                {isOpenImportDialog && (
                    <ImportRecord
                        open={isOpenImportDialog}
                        onClose={() => {
                            setIsOpenImportDialog(false);
                            if (inpuSifRef.current) inpuSifRef.current.value = '';
                            setSelectedSifFile(null);
                        }}
                        sifFile={selectedSifFile}
                        onSubmit={() => {
                            setIsOpenImportDialog(false);
                            if (inpuSifRef.current) inpuSifRef.current.value = '';
                            handleGridRefresh();
                        }}
                        recordTypeId={recordTypeId}
                        apiRef={apiRef}
                        showObjectPanel={showObjectPanel}
                        ObjectPanelContainer={ObjectPanelContainer}
                        objectsToCreate={objectsToCreate}
                    />
                )}
            </>
            <Tooltip title="Refresh">
                <IconButton
                    color="primary"
                    onClick={(e) => {
                        e.preventDefault();
                        handleGridRefresh();
                    }}
                    className={classes.gridOptionsButton}
                >
                    <Refresh fontSize="small" />
                    <Typography variant="body1" className={classes.gridToolbarCustomFont} component="div">
                        Refresh Grid
                    </Typography>
                </IconButton>
            </Tooltip>
            {isAllowedDelete && (
                <Button
                    startIcon={showOnlyEnabledRecords ? <LayersClear /> : <Layers />}
                    size="medium"
                    color="primary"
                    onClick={onToggleShowRecords}
                    sx={({ palette }) => ({
                        fontSize: '13px',
                        color: palette.primary[400],
                        backgroundColor: palette.primary[300],
                        borderRadius: '30px',
                        padding: '8px 16px'
                    })}
                >
                    {showOnlyEnabledRecords ? 'Show' : 'Hide'} delete Records
                </Button>
            )}
            {showResetRowOrder && (
                <Tooltip title="Restore Sort">
                    <IconButton
                        color="primary"
                        onClick={(e) => {
                            e.preventDefault();
                            handleResetRowOrder();
                        }}
                        className={classes.gridOptionsButton}
                    >
                        <SwapVert fontSize="small" />
                        <Typography variant="body1" className={classes.gridToolbarCustomFont} component="div">
                            Sort
                        </Typography>
                        <Clear fontSize="small" className={classes.gridToolbarCustomFontBlack} />
                    </IconButton>
                </Tooltip>
            )}
            {!isSuperRecordType && (
                <>
                    {isAllowedEdit && (
                        <GridToolbarEdit
                            onClick={onClickEdit}
                            show={!!rowsSelected.size || isEditing}
                            isEditing={isEditing}
                            updateLoading={updateLoading}
                        />
                    )}
                    <GridToolbarActions
                        getZipFileUrl={getZipFileUrl}
                        trigger={recordType}
                        onDelete={onDelete}
                        recordType={recordType}
                        rowsSelectedAmount={rowsSelectedAmount === 0 ? rowsSelected.size : rowsSelectedAmount}
                        onClickBulkEdit={onClickBulkEdit}
                        show={(isAllowedDelete && !isAlreadyDeleted) || !!rowsSelected.size}
                        showBulkUpdate={isAllowedUpdate}
                        showBulkDownload={isAllowedDownload}
                        showDelete={isAllowedDelete && !isAlreadyDeleted}
                    />
                </>
            )}

            {isEditing && isAllowedEdit && (
                <>
                    <Tooltip title="Save">
                        <IconButton
                            sx={({ palette }) => ({
                                color: palette.primary[400],
                                backgroundColor: palette.primary[300]
                            })}
                            size="large"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSave();
                            }}
                        >
                            {updateLoading ? <CircularProgress color="inherit" size={14} /> : <Save />}
                        </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Save in all rows">
                        <IconButton color="primary" onClick={editBulk}>
                            <SaveAs />
                        </IconButton>
                    </Tooltip> */}
                </>
            )}
            <div style={{ flexGrow: 1 }} />
        </GridToolbarContainer>
    );
};
